import {
  Zap,
  TrendingUp,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  DollarSign,
} from 'lucide-react'
import type { AIMetrics, GeminiUsage, TopTool, ErrorPattern } from '@/types/dashboard'

interface AIMetricsPanelProps {
  data: AIMetrics | null
  loading: boolean
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}K`
  return tokens.toString()
}

function TokenUsageCard({ usage }: { usage: GeminiUsage }) {
  // Calculate percentage of daily token budget (assume 100k default)
  const dailyBudget = 100_000
  const percentage = Math.min((usage.tokensToday / dailyBudget) * 100, 100)

  return (
    <div className="bg-surface border border-surface-border rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Zap className="w-4 h-4 text-amber-400" />
        <span className="text-sm font-medium text-text">Token Usage</span>
      </div>

      {/* Circular Progress Ring */}
      <div className="relative w-24 h-24 mx-auto mb-3">
        <svg className="w-full h-full -rotate-90">
          <circle
            cx="48"
            cy="48"
            r="40"
            strokeWidth="8"
            stroke="currentColor"
            className="text-surface-border"
            fill="none"
          />
          <circle
            cx="48"
            cy="48"
            r="40"
            strokeWidth="8"
            stroke="currentColor"
            className="text-amber-400"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${percentage * 2.51327} 251.327`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-text">
            {formatTokens(usage.tokensToday)}
          </span>
          <span className="text-[10px] text-text-muted">TODAY</span>
        </div>
      </div>

      {/* Stats */}
      <div className="space-y-1.5 text-xs">
        <div className="flex justify-between text-text-muted">
          <span>This Month</span>
          <span className="text-text font-mono">
            {formatTokens(usage.tokensThisMonth)}
          </span>
        </div>
        <div className="flex justify-between text-text-muted">
          <span className="flex items-center gap-1">
            <DollarSign className="w-3 h-3" />
            Est. Cost
          </span>
          <span className="text-emerald-400 font-mono">
            ${usage.estimatedCostUsd.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Model Breakdown */}
      {usage.modelBreakdown.length > 0 && (
        <div className="mt-3 pt-3 border-t border-surface-border">
          <div className="text-[10px] text-text-muted uppercase mb-1.5">
            By Model
          </div>
          {usage.modelBreakdown.slice(0, 2).map((model) => (
            <div
              key={model.model}
              className="flex justify-between text-xs text-text-muted"
            >
              <span className="truncate">{model.model.replace('gemini-', '')}</span>
              <span className="font-mono">
                {formatTokens(model.inputTokens + model.outputTokens)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function SatisfactionGauge({
  overall,
  positive,
  negative,
}: {
  overall: number
  positive: number
  negative: number
}) {
  const percentage = Math.round(overall * 100)
  const color =
    percentage >= 80
      ? 'text-emerald-400'
      : percentage >= 60
        ? 'text-yellow-400'
        : 'text-red-400'

  return (
    <div className="bg-surface border border-surface-border rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <ThumbsUp className="w-4 h-4 text-emerald-400" />
        <span className="text-sm font-medium text-text">Satisfaction</span>
      </div>

      {/* Semi-circular gauge */}
      <div className="relative w-24 h-12 mx-auto mb-3">
        <svg className="w-full h-full" viewBox="0 0 100 50">
          {/* Background arc */}
          <path
            d="M 10 50 A 40 40 0 0 1 90 50"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            className="text-surface-border"
          />
          {/* Filled arc */}
          <path
            d="M 10 50 A 40 40 0 0 1 90 50"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            className={color}
            strokeLinecap="round"
            strokeDasharray={`${(percentage / 100) * 125.66} 125.66`}
          />
        </svg>
        <div className="absolute inset-0 flex items-end justify-center pb-0">
          <span className={`text-2xl font-bold ${color}`}>{percentage}%</span>
        </div>
      </div>

      {/* Breakdown */}
      <div className="flex justify-center gap-4 text-xs">
        <div className="flex items-center gap-1">
          <ThumbsUp className="w-3 h-3 text-emerald-400" />
          <span className="text-text">{positive}</span>
        </div>
        <div className="flex items-center gap-1">
          <ThumbsDown className="w-3 h-3 text-red-400" />
          <span className="text-text">{negative}</span>
        </div>
      </div>
    </div>
  )
}

function TopToolsCard({ tools }: { tools: TopTool[] }) {
  return (
    <div className="bg-surface border border-surface-border rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-blue-400" />
        <span className="text-sm font-medium text-text">Top Tools</span>
      </div>

      <div className="space-y-2">
        {tools.slice(0, 5).map((tool, idx) => (
          <div key={tool.name} className="flex items-center gap-2">
            <span className="text-[10px] text-text-muted w-4">{idx + 1}.</span>
            <span className="text-xs text-text truncate flex-1">{tool.name}</span>
            <span className="text-xs text-text-muted font-mono">{tool.count}</span>
            {tool.satisfaction !== null && (
              <span
                className={`text-[10px] font-mono ${
                  tool.satisfaction >= 0.8
                    ? 'text-emerald-400'
                    : tool.satisfaction >= 0.6
                      ? 'text-yellow-400'
                      : 'text-red-400'
                }`}
              >
                {Math.round(tool.satisfaction * 100)}%
              </span>
            )}
          </div>
        ))}
        {tools.length === 0 && (
          <div className="text-xs text-text-muted text-center py-2">
            No tool usage data yet
          </div>
        )}
      </div>
    </div>
  )
}

function ErrorPatternsCard({ patterns }: { patterns: ErrorPattern[] }) {
  return (
    <div className="bg-surface border border-surface-border rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-red-400" />
        <span className="text-sm font-medium text-text">Error Patterns</span>
      </div>

      <div className="space-y-2">
        {patterns.slice(0, 4).map((pattern) => (
          <div
            key={pattern.id}
            className="p-2 rounded-lg bg-red-500/5 border border-red-500/10"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-text truncate">{pattern.toolName}</span>
              <span className="text-[10px] text-red-400 font-mono">
                {pattern.occurrenceCount}x
              </span>
            </div>
            <div className="text-[10px] text-text-muted truncate">
              {pattern.errorSample}
            </div>
          </div>
        ))}
        {patterns.length === 0 && (
          <div className="text-xs text-text-muted text-center py-4">
            <AlertTriangle className="w-5 h-5 mx-auto mb-1 opacity-30" />
            No error patterns detected
          </div>
        )}
      </div>
    </div>
  )
}

export default function AIMetricsPanel({ data, loading }: AIMetricsPanelProps) {
  if (loading && !data) {
    return (
      <div className="grid grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-surface border border-surface-border rounded-xl p-4 h-48 flex items-center justify-center"
          >
            <Loader2 className="w-6 h-6 animate-spin text-accent" />
          </div>
        ))}
      </div>
    )
  }

  // Provide defaults for missing data
  const defaultUsage: GeminiUsage = {
    tokensToday: 0,
    tokensThisWeek: 0,
    tokensThisMonth: 0,
    estimatedCostUsd: 0,
    modelBreakdown: [],
    dailyUsage: [],
  }

  const geminiUsage = data?.geminiUsage || defaultUsage
  const satisfaction = data?.satisfaction || {
    overall: 0,
    positive: 0,
    negative: 0,
    neutral: 0,
    lowPerformingTools: [],
  }
  const topTools = data?.toolUsage?.topTools || []
  const errorPatterns = data?.errors?.topPatterns || []

  return (
    <div className="grid grid-cols-2 gap-4">
      <TokenUsageCard usage={geminiUsage} />
      <SatisfactionGauge
        overall={satisfaction.overall}
        positive={satisfaction.positive}
        negative={satisfaction.negative}
      />
      <TopToolsCard tools={topTools} />
      <ErrorPatternsCard patterns={errorPatterns} />
    </div>
  )
}
